import React, { useEffect, useMemo, useState } from "react";
import { api, formatGBP } from "@/lib/api";
import { Calculator, Truck, HandCoins, Scale, Sparkles } from "lucide-react";
import TermsNotice from "@/components/TermsNotice";

export default function Estimator({ onQuote, compact = false }) {
  const [categories, setCategories] = useState([]);
  const [bulky, setBulky] = useState([]);
  const [category, setCategory] = useState("");
  const [weight, setWeight] = useState("");
  const [loadType, setLoadType] = useState("self");
  const [selectedBulky, setSelectedBulky] = useState({});
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, b] = await Promise.all([
        api.get("/pricing/waste-categories"),
        api.get("/pricing/bulky-items"),
      ]);
      setCategories(c.data);
      setBulky(b.data);
      if (c.data.length) setCategory(c.data[0].id);
    })();
  }, []);

  const bulkyEntries = useMemo(
    () =>
      Object.entries(selectedBulky)
        .filter(([, q]) => q > 0)
        .map(([item_id, qty]) => ({ item_id, qty })),
    [selectedBulky]
  );

  const canEstimate = category && Number(weight) > 0;

  useEffect(() => {
    if (!canEstimate) {
      setQuote(null);
      return;
    }
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/quote", {
          waste_category_id: category,
          estimated_weight_kg: Number(weight),
          load_type: loadType,
          bulky_items: bulkyEntries,
        });
        setQuote(data);
        onQuote?.(data, { category, weight, loadType, bulkyEntries });
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [category, weight, loadType, JSON.stringify(bulkyEntries)]);

  const toggleBulky = (id, delta) => {
    setSelectedBulky((s) => ({ ...s, [id]: Math.max(0, (s[id] || 0) + delta) }));
  };

  return (
    <div className="wa-card" data-testid="estimator-widget">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#8DC63F] text-black grid place-items-center">
          <Calculator size={18} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">Instant estimate</div>
          <div className="font-display text-xl font-bold text-white">Weight-based quote</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="wa-label">Waste type</label>
          <select
            className="wa-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="estimator-waste-category"
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {formatGBP(c.sale_per_kg)}/kg
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="wa-label">Estimated weight (kg)</label>
          <input
            type="number"
            min="0"
            className="wa-input"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="e.g. 350"
            data-testid="estimator-weight-input"
          />
        </div>
      </div>

      <div className="mt-3">
        <label className="wa-label">Load type</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setLoadType("self")}
            className={`relative flex flex-col items-start gap-1 px-3 py-3 rounded-xl border text-sm font-semibold transition ${
              loadType === "self"
                ? "border-[#8DC63F] bg-[#8DC63F] text-black"
                : "border-[#232428] bg-black text-white hover:bg-white/5"
            }`}
            data-testid="estimator-load-self"
          >
            <span className="flex items-center gap-2"><HandCoins size={16} /> Self load</span>
            {quote && quote.self_load_saving > 0 && (
              <span
                className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full ${loadType === "self" ? "bg-black/15 text-black" : "bg-[#8DC63F] text-black"}`}
                data-testid="estimator-self-load-saving-badge"
              >
                Save {formatGBP(quote.self_load_saving)}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setLoadType("we")}
            className={`flex flex-col items-start gap-1 px-3 py-3 rounded-xl border text-sm font-semibold transition ${
              loadType === "we"
                ? "border-[#8DC63F] bg-[#8DC63F] text-black"
                : "border-[#232428] bg-black text-white hover:bg-white/5"
            }`}
            data-testid="estimator-load-we"
          >
            <span className="flex items-center gap-2"><Truck size={16} /> We load (roadside)</span>
            {quote && quote.we_load_surcharge_percent > 0 && (
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${loadType === "we" ? "text-black/70" : "text-gray-400"}`}>
                +{quote.we_load_surcharge_percent}% loading surcharge
              </span>
            )}
          </button>
        </div>
      </div>

      {!compact && bulky.length > 0 && (
        <div className="mt-4">
          <label className="wa-label">Bulky items (surcharges)</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {bulky.map((b) => {
              const qty = selectedBulky[b.id] || 0;
              return (
                <div
                  key={b.id}
                  className={`rounded-xl border p-2 text-xs transition ${
                    qty > 0 ? "border-[#8DC63F] bg-[#8DC63F]/5" : "border-[#232428] bg-[#0a0a0a]"
                  }`}
                  data-testid={`estimator-bulky-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="font-semibold text-white leading-tight">{b.name}</div>
                  <div className="text-gray-400">{formatGBP(b.sale_price)} ea</div>
                  <div className="flex items-center justify-between mt-2">
                    <button onClick={() => toggleBulky(b.id, -1)} className="w-6 h-6 rounded-full bg-white/10 text-white">−</button>
                    <span className="font-bold text-white">{qty}</span>
                    <button onClick={() => toggleBulky(b.id, 1)} className="w-6 h-6 rounded-full bg-[#8DC63F] text-black">+</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-5 border-t border-dashed border-[#232428] pt-4">
        {!quote && (
          <div className="text-sm text-gray-400" data-testid="estimator-empty">
            Enter waste type and weight to see your transparent breakdown.
          </div>
        )}
        {quote && (
          <div className="space-y-1.5 text-sm" data-testid="estimator-breakdown">
            <Row label="Weight charge" value={formatGBP(quote.base_weight_charge)} />
            {quote.bulky_total > 0 && <Row label="Bulky item surcharges" value={formatGBP(quote.bulky_total)} />}
            {quote.we_load_surcharge > 0 && (
              <Row
                label={`We-load surcharge (+${quote.we_load_surcharge_percent}%)`}
                value={formatGBP(quote.we_load_surcharge)}
              />
            )}
            {quote.loading_fee > 0 && <Row label="We-load loading fee" value={formatGBP(quote.loading_fee)} />}
            {quote.self_load_discount > 0 && (
              <Row label="Self-load discount" value={`− ${formatGBP(quote.self_load_discount)}`} />
            )}
            {quote.min_charge_applied && (
              <div className="text-xs text-gray-400" data-testid="estimator-min-applied">Minimum collection charge applied.</div>
            )}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-[#232428]">
              <div className="font-display text-xl font-bold text-white">Estimated total</div>
              <div className="font-display text-3xl font-black text-[#8DC63F]" data-testid="estimator-total">
                {formatGBP(quote.estimated_total)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="rounded-lg bg-[#0a0a0a] border border-[#232428] p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">Deposit due now</div>
                <div className="font-bold text-lg text-white" data-testid="estimator-deposit">{formatGBP(quote.deposit_due)}</div>
              </div>
              <div className="rounded-lg bg-[#0a0a0a] border border-[#232428] p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">Balance after weighing</div>
                <div className="font-bold text-lg text-white" data-testid="estimator-balance">{formatGBP(quote.balance_due)}</div>
              </div>
            </div>

            {quote.suggested_skip && (
              <InlineSkipCompare quote={quote} />
            )}

            <TermsNotice variant="inline" />
          </div>
        )}
        {loading && <div className="text-xs text-gray-500 mt-2">Updating…</div>}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400">{label}</span>
      <span className="font-semibold text-white">{value}</span>
    </div>
  );
}

function InlineSkipCompare({ quote }) {
  const s = quote.suggested_skip;
  const mid = (s.price_low + s.price_high) / 2;
  const saving = Math.max(0, mid - quote.estimated_total);
  return (
    <div
      className="mt-4 rounded-xl border-2 border-[#8DC63F] bg-[#8DC63F]/8 p-3"
      style={{ background: "rgba(141, 198, 63, 0.08)" }}
      data-testid="inline-skip-comparison"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#8DC63F]">
        <Scale size={14} /> Price comparison
      </div>
      <p className="text-xs text-gray-200 mt-1.5">
        Based on your estimated waste, you may need a{" "}
        <span className="font-bold text-white">{s.name}</span> costing around{" "}
        <span className="font-bold text-white">
          {formatGBP(s.price_low)}–{formatGBP(s.price_high)}
        </span>
        . With Weigh Away your estimated collection is{" "}
        <span className="font-bold text-[#8DC63F]">{formatGBP(quote.estimated_total)}</span>, and you only pay for what we take.
      </p>
      {saving > 0 && (
        <div
          className="mt-3 rounded-xl bg-black border-2 border-[#8DC63F] p-4 text-center relative overflow-hidden"
          data-testid="inline-skip-savings-card"
          style={{ boxShadow: "0 8px 28px rgba(141, 198, 63, 0.25)" }}
        >
          <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#8DC63F] font-bold">
            <Sparkles size={14} /> You could save
          </div>
          <div
            className="font-display font-black text-[#8DC63F] leading-none mt-1.5"
            style={{ fontSize: "clamp(2.5rem, 6vw, 3.5rem)" }}
            data-testid="inline-skip-savings"
          >
            {formatGBP(saving)}
          </div>
          <div className="text-[11px] text-gray-400 mt-1.5">
            vs the typical mid-price of a {s.name.toLowerCase()}
          </div>
        </div>
      )}
    </div>
  );
}
